import type { PanelThemeColors } from '@/store/slices/themeSlice';
import type { ThemeType } from '@/interfaces/interfaces';
import { darkColorsTheme } from '../dark/darkTheme';
import { lightColorsTheme } from '../light/lightTheme';
import type { ITheme } from '../types';

function toRgb(color: string) {
  return [1, 3, 5].map((offset) =>
    Number.parseInt(color.slice(offset, offset + 2), 16)
  );
}

function toHex(value: number) {
  return Math.round(value).toString(16).padStart(2, '0');
}

function mix(first: string, second: string, secondWeight: number) {
  const firstRgb = toRgb(first);
  const secondRgb = toRgb(second);

  return `#${firstRgb
    .map((channel, index) =>
      toHex(channel * (1 - secondWeight) + secondRgb[index] * secondWeight)
    )
    .join('')}`;
}

function readableText(background: string) {
  const [red, green, blue] = toRgb(background);
  const luminance = (red * 299 + green * 587 + blue * 114) / 255000;

  return luminance < 0.52 ? '#F8FAFC' : '#172033';
}

export function buildCustomTheme(
  colors: PanelThemeColors,
  resolvedTheme: ThemeType
): ITheme {
  const base = resolvedTheme === 'dark' ? darkColorsTheme : lightColorsTheme;
  const sidebarText = readableText(colors.sidebar);
  const border = mix(colors.surface, colors.text, 0.16);
  const secondaryBackground = mix(colors.background, colors.text, 0.035);

  return {
    ...base,
    logoText: sidebarText,
    logoTextSpan: colors.primary,
    primary: colors.primary,
    primary1: mix(colors.surface, colors.primary, 0.12),
    primaryGradient: `linear-gradient(135deg, ${colors.primary} 0%, ${mix(
      colors.primary,
      base.secondary,
      0.42
    )} 100%)`,
    secondaryGradient: `linear-gradient(135deg, ${colors.surface} 0%, ${mix(
      colors.background,
      colors.primary,
      0.12
    )} 100%)`,
    background: colors.surface,
    secondaryBackground,
    secondaryBackgroundSelected: mix(secondaryBackground, colors.primary, 0.1),
    additionalBackground: mix(colors.surface, colors.text, 0.035),
    timelineBackground: colors.background,
    siderBackground: colors.sidebar,
    collapseBackground: colors.primary,
    spinnerBase: colors.primary,
    scroll: mix(colors.background, colors.text, 0.25),
    border,
    borderNft: mix(colors.surface, colors.text, 0.24),
    textMain: colors.text,
    textLight: mix(colors.text, colors.surface, 0.38),
    textSuperLight: mix(colors.text, colors.surface, 0.55),
    textSecondary: mix(colors.text, colors.surface, 0.16),
    textDark: colors.text,
    textNftLight: mix(colors.text, colors.surface, 0.38),
    textSiderPrimary: mix(sidebarText, colors.primary, 0.3),
    textSiderSecondary: mix(sidebarText, colors.sidebar, 0.2),
    subText: mix(colors.text, colors.surface, 0.38),
    shadow:
      resolvedTheme === 'dark'
        ? 'rgba(0, 0, 0, 0.38)'
        : 'rgba(15, 23, 42, 0.1)',
    boxShadow:
      resolvedTheme === 'dark'
        ? '0 1px 4px rgba(0, 0, 0, 0.4)'
        : '0 1px 4px rgba(15, 23, 42, 0.1)',
    boxShadowHover:
      resolvedTheme === 'dark'
        ? '0 12px 28px rgba(0, 0, 0, 0.48)'
        : '0 12px 28px rgba(15, 23, 42, 0.14)',
    heading: colors.text,
    borderBase: border,
    disable: mix(colors.text, colors.surface, 0.56),
    disabledBg: mix(colors.surface, colors.text, 0.08),
    layoutBodyBg: colors.background,
    layoutHeaderBg: colors.background,
    layoutSiderBg: colors.sidebar,
    inputPlaceholder: mix(colors.text, colors.surface, 0.52),
    itemHoverBg: mix(colors.sidebar, colors.primary, 0.17),
    backgroundColorBase: mix(colors.surface, colors.text, 0.07),
    avatarBg: mix(colors.surface, colors.text, 0.1),
    alertTextColor: readableText(colors.primary),
    breadcrumb: mix(colors.text, colors.surface, 0.38),
    icon: mix(colors.text, colors.surface, 0.38),
    iconHover: colors.text
  };
}
